import { liveNowPath } from '@/modules/instant-sessions/liveNowPath'
import { computed, markRaw, type Component, type Ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import HomeIcon from '~icons/lucide/house'
import SearchIcon from '~icons/lucide/search'
import SparklesIcon from '~icons/lucide/sparkles'
import RadioIcon from '~icons/lucide/radio'
import SessionsIcon from '~icons/lucide/calendar-clock'
import MessagesIcon from '~icons/lucide/message-square-text'
import GemIcon from '~icons/lucide/gem'
import NotebookIcon from '~icons/lucide/notebook-pen'
import PeopleIcon from '~icons/lucide/users-round'
import MapPinIcon from '~icons/lucide/map-pin'
import CalendarDaysIcon from '~icons/lucide/calendar-days'
import UtensilsIcon from '~icons/lucide/utensils'
import PlaneIcon from '~icons/lucide/plane'
import HelpRequestsIcon from '~icons/lucide/message-square-plus'
import WorkIcon from '~icons/lucide/briefcase'
import RocketIcon from '~icons/lucide/rocket'
import CompassIcon from '~icons/lucide/compass'
import QuoteIcon from '~icons/lucide/message-square-quote'
import HiringIcon from '~icons/lucide/briefcase-business'
import ArmchairIcon from '~icons/lucide/armchair'
import TablesIcon from '~icons/tabler/tank'
import MemoryIcon from '~icons/lucide/brain'
import DirectionIcon from '~icons/lucide/signpost'
import KnowledgeIcon from '~icons/lucide/brain-circuit'
import TvIcon from '~icons/lucide/tv'
import RecordingsIcon from '~icons/lucide/circle-play'
import StatsIcon from '~icons/lucide/chart-no-axes-column'
import FileTextIcon from '~icons/lucide/file-text'
import CircleDollarSignIcon from '~icons/lucide/circle-dollar-sign'
import { ACTOR_TYPE, ROLE } from '@/shared/data/permissionSystem'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import type { ComingSoonKey } from './comingSoon'
import type { SidebarCounts } from './useSidebarCounts'
import { mentorTourRealPath } from '@/modules/mentor-tour/demoMode'

export type SidebarItem = {
  key: string
  label: string
  icon: Component
  href?: string
  // Path prefixes that mark the item as the current page.
  match?: string[]
  // Paths that mark the item as the current page only as they are, not the
  // paths below them.
  exact?: string[]
  comingSoon?: ComingSoonKey
  badge?: number | null
  // A feature in testing: the row shows a Beta tag at all times.
  beta?: boolean
  // The green dot of "Live now", and the number of mentors live now.
  isLive?: boolean
  liveCount?: number
  onClick?: () => void
  // A blue dot after the label: something waits for the person there.
  hasDot?: boolean
  children?: SidebarSubItem[]
}

export type SidebarSubItem = {
  key: string
  label: string
  // The More sheet shows a sub-item as a row of its own, with this icon.
  icon?: Component
  href?: string
  match?: string[]
  exact?: string[]
  comingSoon?: ComingSoonKey
  isLive?: boolean
  // A count of new things on the page of the sub-item.
  badge?: number | null
}

// The top bar still links these from the avatar menu. The sidebar shows them
// as items, so the profile menu leaves them out. /matches has no item in a
// menu. Roadmap is the one AI matching entry. Lists has no sidebar row, so it
// stays in the profile menu: members could not find their saved mentors
// (Jessica, 29 Oct).
export const PROMOTED_HREFS = ['/roadmap', '/matches', '/leaderboard']

// GrowthExpert goes away; Work replaces it. The sidebar's profile menu leaves
// it out now. The old top bar keeps it until the kill.
export const RETIRED_HREFS = ['/growthexpert']

// The places of the phone tab bar (MobileTabBar). The More sheet leaves them
// out, and shows their sub-items as rows instead.
export const TAB_BAR_KEYS = ['home', 'mentors', 'sessions', 'messages']

// Off: the sidebar lists only live features and every Soon item hides.
// On: the whole suite shows, and a Soon item opens the coming-soon window.
export const SHOW_COMING_SOON = false

const withoutSoon = <T extends { comingSoon?: unknown }>(items: T[]) =>
  SHOW_COMING_SOON ? items : items.filter((item) => !item.comingSoon)

const icon = (component: Component) => markRaw(component)

// A page of the mentor tour has the address /mentor-tour. For the sidebar it
// is the page of the member app that it shows.
export const pathOf = (url: string) =>
  mentorTourRealPath(url.split('?')[0].split('#')[0])

export const isPathMatch = (url: string, prefixes: string[] = []) => {
  const path = pathOf(url)
  return prefixes.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
}

type Actions = {
  markMessagesAsRead: () => void
}

type FeatureFlags = {
  instantSessionsEnabled?: boolean
  citySquadsEnabled?: boolean
  cityDinnersEnabled?: boolean
  askEnabled?: boolean
  asksEnabled?: boolean
  workEnabled?: boolean
  adventuresEnabled?: boolean
  mentorStatsEnabled?: boolean
  hiringEnabled?: boolean
  advisoryBoardEnabled?: boolean
  tablesEnabled?: boolean
  sessionPrepAndTakeawaysEnabled?: boolean
  takeawaysEnabled?: boolean
  mentorChatEnabled?: boolean
  questionOptInEnabled?: boolean
  mentorKnowledgeEnabled?: boolean
  // The exoskeleton (ExoskeletonFlags). A journey flag is on only with Base.
  exoBaseEnabled?: boolean
  exoLiveEnabled?: boolean
  exoCommunityEnabled?: boolean
  exoAdvisorSeatsEnabled?: boolean
  exoSprintsEnabled?: boolean
  exoGuestPostsEnabled?: boolean
  exoLiveHubEnabled?: boolean
}

// Everything the sidebar lists, in order. A feature gets its own row when
// members use it every week and it is a place. Mentors and Sessions are one
// row each with no sub-items (Foti, 23 Oct): Lists, Leaderboard and AI
// Matching stay open by their links, and the pages of Sessions open from the
// tabs of the Sessions page (useSessionsTabs). Recordings is a row of its
// own, under Sessions, in both modes (Foti, 25 Oct). Your stats is a row of
// its own in Mentor mode, behind its flag (Foti, 28 Oct). Notifications is the
// bell in the sidebar head, not a row.
//
// An item with `comingSoon` is not built yet: it shows a Soon tag and opens
// the coming-soon window. When the feature ships, its own PR swaps
// `comingSoon` for an `href` behind its flag.
export const useSidebarNavigation = (
  counts: Ref<SidebarCounts>,
  actions: Actions,
) => {
  const page = usePage()
  const { accessControl, hasRole } = usePermissionSystem()

  const isMentorRole = computed(() => hasRole(ROLE.MENTOR))
  const featureFlags = computed(
    () =>
      (page.props.shared as { featureFlags?: FeatureFlags }).featureFlags ?? {},
  )
  const actorType = computed(() => accessControl.value.actorType)
  // How many lives are on air now: the green dot on Live.
  const liveOnAir = () =>
    Number((page.props.shared as { liveOnAir?: number }).liveOnAir) || 0
  // Earn has a request, a kickoff call or late work (Monetisation::NeedsYou).
  const earnNeedsYou = computed(
    () =>
      !!(page.props.shared as { sidebar?: { earnNeedsYou?: boolean } | null })
        .sidebar?.earnNeedsYou,
  )
  const ownCommunity = computed(
    () =>
      (
        page.props.shared as {
          sidebar?: {
            ownCommunity?: { chat: string; vault: string; members: string }
          } | null
        }
      ).sidebar?.ownCommunity ?? null,
  )

  const sessionsHref = computed(() => {
    if (actorType.value === ACTOR_TYPE.MENTOR) return '/mentor/sessions'
    if (actorType.value === ACTOR_TYPE.MENTEE) return '/sessions'
    return undefined
  })

  // Knowledge is the map of what a mentor knows and how they mentor, read
  // from their recorded calls. Mentor mode only, behind its flag.
  const hasKnowledge = computed(
    () =>
      actorType.value === ACTOR_TYPE.MENTOR &&
      !!featureFlags.value.mentorKnowledgeEnabled,
  )

  // The mentor's answers on the question pages, once the mentor has one.
  const hasAnswers = computed(
    () => actorType.value === ACTOR_TYPE.MENTOR && !!counts.value.answers,
  )
  // With the opt-in rule of the question pages, Answers is a main row with a
  // Beta tag (see `main`), not a tab of Sessions.
  const isAnswersMainRow = computed(
    () => hasAnswers.value && !!featureFlags.value.questionOptInEnabled,
  )

  const main = computed<SidebarItem[]>(() => {
    const items: SidebarItem[] = [
      {
        key: 'home',
        label: 'Home',
        icon: icon(HomeIcon),
        href: '/dashboard',
        match: ['/dashboard'],
      },
    ]
    // Knowledge is the mentor's own page, so it sits right under Home.
    if (hasKnowledge.value) {
      items.push({
        key: 'knowledge',
        label: 'Knowledge',
        icon: icon(KnowledgeIcon),
        href: '/mentor/knowledge',
        match: ['/mentor/knowledge'],
        beta: true,
      })
    }
    // Live now has its own page: the mentors free to talk now, the Go live
    // card for a mentor, and an explanation when nobody is live. The dot and
    // the number show only while a mentor is live.
    if (featureFlags.value.instantSessionsEnabled) {
      const liveMentors = counts.value.liveMentors ?? 0
      items.push({
        key: 'live_now',
        label: 'Live now',
        icon: icon(RadioIcon),
        href: liveNowPath(featureFlags.value),
        match: [liveNowPath(featureFlags.value)],
        isLive: liveMentors > 0,
        liveCount: liveMentors,
      })
    }
    items.push({
      key: 'mentors',
      label: 'Mentors',
      icon: icon(SearchIcon),
      href: '/search',
      match: ['/search', '/matches', '/lists', '/mentors', '/leaderboard'],
    })
    // AI matching, its own row right under Mentors again (Foti, 4 Nov): the
    // mentor chat, or the Roadmap builder without it.
    items.push({
      key: 'ai_matching',
      label: 'AI matching',
      icon: icon(SparklesIcon),
      href: featureFlags.value.mentorChatEnabled ? '/ai-matching' : '/roadmap',
      match: ['/ai-matching', '/roadmap', '/roadmaps'],
    })
    if (sessionsHref.value) {
      items.push({
        key: 'sessions',
        label: 'Sessions',
        icon: icon(SessionsIcon),
        href: sessionsHref.value,
        match: [
          '/sessions',
          '/mentor/sessions',
          '/takeaways',
          '/mentor/impact',
          ...(isAnswersMainRow.value ? [] : ['/mentor/answers']),
          '/notes',
          '/mentor/notes',
        ],
        badge: counts.value.pendingSessionRequests,
      })
      // One list for both modes: a mentor sees the calls where they were the
      // mentor and the calls where they were the mentee.
      items.push({
        key: 'recordings',
        label: 'Recordings',
        icon: icon(RecordingsIcon),
        href: '/recordings',
        match: ['/recordings'],
      })
    }
    // The mentor's own numbers, in Mentor mode only.
    if (
      actorType.value === ACTOR_TYPE.MENTOR &&
      featureFlags.value.mentorStatsEnabled
    ) {
      items.push({
        key: 'stats',
        label: 'Your stats',
        icon: icon(StatsIcon),
        href: '/mentor/stats',
        match: ['/mentor/stats'],
      })
    }
    // Memory is the member's brief and the mentors' advice across all the
    // calls. The row shows from the first recorded call, with the number of
    // lines that are new in the brief.
    // Direction is its own page next to Memory. The row shows for every
    // member with the flag, with a badge after a rewrite not seen yet.
    if (
      actorType.value === ACTOR_TYPE.MENTEE &&
      counts.value.directionNew !== null
    ) {
      items.push({
        key: 'direction',
        label: 'Direction',
        icon: icon(DirectionIcon),
        href: '/direction',
        match: ['/direction'],
        badge: counts.value.directionNew,
        beta: true,
      })
    }
    if (
      actorType.value === ACTOR_TYPE.MENTEE &&
      counts.value.memoryNewLines !== null
    ) {
      items.push({
        key: 'memory',
        label: 'Memory',
        icon: icon(MemoryIcon),
        href: '/memory',
        match: ['/memory'],
        badge: counts.value.memoryNewLines,
        beta: true,
      })
    }
    items.push({
      key: 'messages',
      label: 'Messages',
      icon: icon(MessagesIcon),
      href: '/conversations',
      match: ['/conversations'],
      badge: counts.value.messages,
      onClick: actions.markMessagesAsRead,
    })
    // Help requests is a core place, not an experiment, so it sits in the
    // main list. Ask replaces it once its flag is on, as in the top bar.
    // Asks (help requests v2) takes the same place once its flag is on.
    items.push(
      featureFlags.value.asksEnabled
        ? {
            key: 'asks',
            label: 'Asks',
            icon: icon(HelpRequestsIcon),
            href: '/asks',
            match: ['/asks', '/opportunities'],
          }
        : featureFlags.value.askEnabled
          ? {
              key: 'ask',
              label: 'Ask',
              icon: icon(HelpRequestsIcon),
              href: '/ask',
              match: ['/ask'],
            }
          : {
              key: 'help_requests',
              label: 'Help requests',
              icon: icon(HelpRequestsIcon),
              href: '/opportunities',
              match: [
                '/opportunities',
                '/my-opportunities',
                '/applied-opportunities',
              ],
            },
    )
    // The mentor's answers on the question pages, while the opt-in rule is
    // in testing: a main row of its own, with a Beta tag.
    if (isAnswersMainRow.value) {
      items.push({
        key: 'answers',
        label: 'Answers',
        icon: icon(QuoteIcon),
        href: '/mentor/answers',
        match: ['/mentor/answers'],
        beta: true,
      })
    }
    if (isMentorRole.value) {
      items.push({
        key: 'blue_chip',
        label: 'Blue Chip',
        icon: icon(GemIcon),
        comingSoon: 'blue_chip',
      })
      if (!featureFlags.value.mentorStatsEnabled) {
        items.push({
          key: 'practice',
          label: 'Your practice',
          icon: icon(NotebookIcon),
          comingSoon: 'practice',
        })
      }
    }
    if (featureFlags.value.exoLiveHubEnabled) return liveHubOrder(items)
    return withoutSoon(
      featureFlags.value.exoBaseEnabled ? exoskeletonOrder(items) : items,
    )
  })

  // Live replaces Studio (HQ 1f871fd6): Home, Live, Mentors, Sessions,
  // Recordings, Messages, and Blog posts, Earn and Your stats in Mentor mode.
  // Blog posts sit right under Live (Foti, 30 Oct). Earn is open with no live
  // first (Foti, 23 Oct). Live now stays its own row under Mentors: the strip
  // on /live shows only while a mentor is live, so it was the only way in
  // (Foti, 27 Oct).
  const liveHubOrder = (items: SidebarItem[]): SidebarItem[] => {
    const byKey = new Map(
      exoskeletonOrder(items).map((item) => [item.key, item]),
    )
    const rows: (SidebarItem | undefined)[] = [
      byKey.get('home'),
      {
        key: 'live',
        label: 'Live',
        icon: icon(TvIcon),
        href: '/live',
        match: ['/live'],
        // The green dot while a live is on air (shared liveOnAir).
        isLive: liveOnAir() > 0,
        liveCount: liveOnAir(),
      },
      actorType.value === ACTOR_TYPE.MENTOR
        ? {
            key: 'posts',
            label: 'Blog posts',
            icon: icon(FileTextIcon),
            href: '/posts',
            match: ['/posts'],
          }
        : undefined,
      byKey.get('mentors'),
      byKey.get('ai_matching'),
      byKey.get('live_now'),
      byKey.get('sessions'),
      byKey.get('recordings'),
      byKey.get('messages'),
      byKey.get('monetisation'),
      byKey.get('stats'),
    ]
    return withoutSoon(rows.filter((row): row is SidebarItem => !!row))
  }

  // The exoskeleton sidebar (HQ card d992aa3b): Home, Live, Mentors,
  // Community, then Sessions and Messages. Mentor mode adds Monetisation
  // under Home (and Knowledge). Live now sits under Mentors.
  // The rows of today stay where they are. Each journey turns on its own row with its
  // flag; the row of a journey that is off does not show.
  const exoskeletonOrder = (items: SidebarItem[]): SidebarItem[] => {
    const flags = featureFlags.value
    // Live now stays its own row, right under Mentors: Mentors has no
    // sub-items.
    const liveNow = items.find((item) => item.key === 'live_now')
    const aiMatching = items.find((item) => item.key === 'ai_matching')
    const rows = items.filter(
      (item) => item.key !== 'live_now' && item.key !== 'ai_matching',
    )
    const isMentorMode = actorType.value === ACTOR_TYPE.MENTOR
    const afterHome: SidebarItem[] = [
      ...(isMentorMode &&
      (flags.exoAdvisorSeatsEnabled || flags.exoSprintsEnabled)
        ? [
            {
              key: 'monetisation',
              // Shown as Earn (Foti, 21 Oct); the paths stay /monetisation.
              label: 'Earn',
              icon: icon(CircleDollarSignIcon),
              href: '/monetisation',
              match: ['/monetisation'],
              // One page since 23 Oct (Foti, HQ 89adb007): no sub-pages.
              // A blue dot when a client or a request needs the mentor.
              // In beta for the waves of 23 Oct.
              hasDot: earnNeedsYou.value,
              beta: true,
            },
          ]
        : []),
      ...(flags.exoLiveEnabled
        ? [
            {
              key: 'live',
              label: 'Live',
              icon: icon(TvIcon),
              // Live now moved to /mentors/live-now with this flag.
              href: '/live',
              match: ['/live'],
              isLive: liveOnAir() > 0,
              liveCount: liveOnAir(),
            },
          ]
        : []),
    ]
    // In Mentor mode, a mentor with a community gets its pages as sub-pages
    // (Chat, Vault, Members), then All communities. A member gets the one row.
    const own = ownCommunity.value
    const community: SidebarItem[] = flags.exoCommunityEnabled
      ? [
          {
            key: 'community',
            label: 'Community',
            icon: icon(PeopleIcon),
            href: isMentorMode && own ? own.chat : '/communities',
            match: ['/communities'],
            ...(isMentorMode && own
              ? {
                  children: [
                    { key: 'community_chat', label: 'Chat', href: own.chat },
                    { key: 'community_vault', label: 'Vault', href: own.vault },
                    {
                      key: 'community_members',
                      label: 'Members',
                      href: own.members,
                    },
                    {
                      key: 'community_all',
                      label: 'All communities',
                      href: '/communities',
                    },
                  ],
                }
              : {}),
          },
        ]
      : []
    // Knowledge stays right under Home, so the new rows come after it.
    const anchor = rows.some((item) => item.key === 'knowledge')
      ? 'knowledge'
      : 'home'
    return rows.flatMap((item) => {
      if (item.key === anchor) return [item, ...afterHome]
      if (item.key === 'mentors') {
        return [
          item,
          ...(aiMatching ? [aiMatching] : []),
          ...(liveNow ? [liveNow] : []),
          ...community,
        ]
      }
      return [item]
    })
  }

  // The main list of the More sheet on a phone. The tab bar already shows
  // Home, Mentors, Sessions and Messages, so the sheet leaves them out. Their
  // sub-items become rows in the same place, less the one that opens the tab's
  // own page (Browse, All sessions).
  const sheetMain = computed<SidebarItem[]>(() =>
    main.value.flatMap((item) => {
      if (!TAB_BAR_KEYS.includes(item.key)) return [item]
      return (item.children ?? [])
        .filter((child) => child.href !== item.href)
        .map((child) => ({
          key: child.key,
          label: child.label,
          icon: child.icon ?? item.icon,
          href: child.href,
          match: child.match,
          exact: child.exact,
          comingSoon: child.comingSoon,
          isLive: child.isLive,
        }))
    }),
  )

  // The Labs group: the features still in testing, one row each, behind
  // their flags (tables, city-squads, hiring, work, adventures). Internal testers go
  // here to see what is new. Its label shows only when two or more items are
  // visible; a single item sits in the list without a label. Each live row
  // has a Beta tag, as Memory and Answers have in the main list.
  // The exoskeleton has no Labs group. The pages stay open by their links.
  const labs = computed<SidebarItem[]>(() =>
    featureFlags.value.exoBaseEnabled ? [] : labsRows.value,
  )

  const labsRows = computed<SidebarItem[]>(() =>
    withoutSoon<SidebarItem>([
      // Think tanks: small online groups around one topic, behind the
      // tables flag.
      ...(featureFlags.value.tablesEnabled
        ? [
            {
              key: 'tables',
              label: 'Think tanks',
              icon: icon(TablesIcon),
              href: '/think-tanks',
              match: ['/think-tanks'],
            },
          ]
        : []),
      // City Squads keeps the `cities` key: it counts demand as
      // coming_soon_cities until the city-squads flag opens the page.
      featureFlags.value.citySquadsEnabled
        ? {
            key: 'cities',
            label: 'City Squads',
            icon: icon(MapPinIcon),
            href: '/cities',
            match: ['/cities'],
            // The network directories. Every child matches /cities; the
            // longest match wins, so a city page lights Cities. An inner
            // page names its own row in the `sidebarActive` prop: a dinner
            // lights Dinners, an event lights Events, a trip view Visiting.
            children: [
              {
                key: 'city_list',
                label: 'Cities',
                icon: icon(MapPinIcon),
                href: '/cities',
                match: ['/cities'],
              },
              {
                key: 'city_events',
                label: 'Events',
                icon: icon(CalendarDaysIcon),
                href: '/cities/events',
                // The host form, the host terms, the staff review and an
                // invitation to speak are pages of Events.
                match: [
                  '/cities/events',
                  '/cities/host',
                  '/cities/review',
                  '/cities/speaker_invitations',
                ],
              },
              ...(featureFlags.value.cityDinnersEnabled
                ? [
                    {
                      key: 'city_dinners',
                      label: 'Dinners',
                      icon: icon(UtensilsIcon),
                      href: '/cities/dinners',
                      match: ['/cities/dinners'],
                    },
                  ]
                : []),
              {
                key: 'city_visiting',
                label: 'Visiting',
                icon: icon(PlaneIcon),
                href: '/cities/visiting',
                match: ['/cities/visiting'],
              },
            ],
          }
        : {
            key: 'cities',
            label: 'City Squads',
            icon: icon(MapPinIcon),
            comingSoon: 'cities',
          },
      featureFlags.value.workEnabled
        ? {
            key: 'work',
            label: 'Work',
            icon: icon(WorkIcon),
            href: '/work',
            match: ['/work'],
          }
        : {
            key: 'work',
            label: 'Work',
            icon: icon(WorkIcon),
            comingSoon: 'work',
          },
      // The Advisory board, behind the advisory-board flag: the member's
      // table, or the mentor's seats at other members' tables.
      ...(featureFlags.value.advisoryBoardEnabled
        ? [
            actorType.value === ACTOR_TYPE.MENTOR
              ? {
                  key: 'board_seats',
                  label: 'Board seats',
                  icon: icon(ArmchairIcon),
                  href: '/board-seats',
                  match: ['/board-seats'],
                  badge: counts.value.boardInvites,
                }
              : {
                  key: 'advisory_board',
                  label: 'Advisory board',
                  icon: icon(ArmchairIcon),
                  href: '/board',
                  match: ['/board'],
                },
          ]
        : []),
      // Hiring is behind the hiring flag (LaunchDarkly).
      ...(featureFlags.value.hiringEnabled
        ? [
            {
              key: 'hiring',
              label: 'Hiring',
              icon: icon(HiringIcon),
              href: '/hiring',
              match: ['/hiring'],
              children: [
                {
                  key: 'hiring_board',
                  label: 'Board',
                  href: '/hiring',
                  // /hiring is the board, but /hiring/mine and
                  // /hiring/opportunities are rows of their own, so the board
                  // takes /hiring as it is and the pages of a listing.
                  exact: ['/hiring'],
                  match: [
                    '/hiring/listings',
                    '/hiring/startups',
                    '/startups',
                    '/hiring/new',
                    '/hiring/interviews',
                  ],
                },
                {
                  key: 'hiring_mine',
                  label: 'My listings',
                  href: '/hiring/mine',
                  match: ['/hiring/mine'],
                },
                {
                  key: 'hiring_opportunities',
                  label: 'Opportunities',
                  href: '/hiring/opportunities',
                  match: ['/hiring/opportunities'],
                },
              ],
            },
          ]
        : []),
      {
        key: 'people',
        label: 'People',
        icon: icon(PeopleIcon),
        comingSoon: 'people',
      },
      {
        key: 'startups',
        label: 'Startups',
        icon: icon(RocketIcon),
        comingSoon: 'startups',
      },
      featureFlags.value.adventuresEnabled
        ? {
            key: 'adventures',
            label: 'Adventures',
            icon: icon(CompassIcon),
            href: '/adventures',
            match: ['/adventures'],
          }
        : {
            key: 'adventures',
            label: 'Adventures',
            icon: icon(CompassIcon),
            comingSoon: 'adventures',
          },
    ]).map((item) => (item.comingSoon ? item : { ...item, beta: true })),
  )

  // The one purple button. A member asks for help: it opens the Ask composer
  // once Ask is on, the way the top bar's Ask button does; before that, a new
  // help request. In Mentor mode it reads Help someone and opens the help
  // requests to answer, for every mentor (Foti, 23 Oct). Lives open from the
  // Live row. /help-requests is the public sales page, not the list.
  const cta = computed(() => {
    if (actorType.value === ACTOR_TYPE.MENTOR) {
      const href = featureFlags.value.asksEnabled ? '/asks' : '/opportunities'
      return { label: 'Help someone', href, isMentor: true }
    }
    // Ask for help opens help requests, also with the new mentee experience:
    // "Not right? Tell us what changed" stays under the path map (Foti,
    // 27 Oct). A free user lands on the locked help requests page.
    if (featureFlags.value.asksEnabled) {
      return { label: 'Ask for help', href: '/asks/new', isMentor: false }
    }
    const isAskOn = featureFlags.value.askEnabled
    return {
      label: 'Ask for help',
      href: isAskOn ? '/ask/new' : '/opportunities/new',
      isMentor: false,
    }
  })

  const isActive = (item: {
    match?: string[]
    exact?: string[]
    href?: string
  }) =>
    !!item.exact?.includes(pathOf(page.url)) ||
    isPathMatch(
      page.url,
      item.match ?? (item.href && !item.exact ? [item.href] : []),
    )

  return { main, sheetMain, labs, cta, isActive }
}
